// CONVERT v2 — the frame both conversion forms share (UI rebuild batch 3; Astra's convert-party / convert-desk).
//
// Astra's anatomy: the form in one card, constrained (≈600 px at lg), the fields first, "More" folded, a hairline, then
// the one primary at the end; at lg a second column, "Before confirming", that reads the conversion back (the party,
// the accounts and the effect) — on the phone the same review follows the fields, above the primary. The primary is
// "Review conversion"; pressed, it becomes the one irreversible confirm naming the figure (Astra's acceptance check:
// "a complete readback before the single irreversible confirmation"). Nothing here posts or decides: the form passes
// what to show.
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DESK_LABEL, type Desk } from '../../DeskSwitch';
import { fmtTime } from '../../../lib/format';
import { Fact } from '../kit';

/** The card and its column. Before Review the card holds the fields and the column (from xl) holds what the form works
 *  on and the desk facts with About this job; ON REVIEW the card's fields give way to the readback itself — one compact
 *  summary right above the confirm, never the whole form with its details repeated under it (1 Nov 2026 audit) — and
 *  Edit brings the fields back. `review` is the Review block: with null children it is only the facts and About. */
export function FormFrame({ children, review, reviewReady, reviewing = false, footer, outcome, side, warnings, notices }: {
  children: ReactNode;
  review: ReactNode;
  /** The prerequisite reads that failed (accounts, parties, balances), each with its Try again. While editing the fields
   *  say them beside the field; on Review the fields are put away, so these say them above the readback (Astra, 1 Nov
   *  2026: a read failing after Review was pressed went unsaid). */
  notices?: ReactNode;
  /** The form's live warnings (a rate off the band, an amount over the balance): in the fields while editing, and kept
   *  beside the confirm on Review — Review never hides a warning (Astra, 1 Nov 2026). */
  warnings?: ReactNode;
  /** What the form works on, beside it from xl (a party's balances) and below xl above the form's fields. */
  side?: ReactNode;
  /** There is a read-back to show. */
  reviewReady: boolean;
  /** Review was pressed: the card shows the readback in the fields' place. */
  reviewing?: boolean;
  footer: ReactNode;
  outcome?: ReactNode;
}) {
  const inReview = reviewing && reviewReady;
  // No column for a form with nothing beside it (the desk form): About this job goes under the action at every width —
  // never a column holding one folded row (1 Nov 2026 audit, P7).
  if (!side) {
    return (
      <section className="space-y-4 rounded-[10px] border border-stroke bg-panel p-4 lg:space-y-5 lg:p-6 xl:max-w-[600px]">
        {inReview ? <>{notices}{review}{warnings}</> : children}
        <div className="border-t border-stroke-soft pt-4 lg:flex lg:items-center lg:justify-end lg:gap-4 lg:pt-5">{footer}</div>
        {!inReview && review}
        {outcome}
      </section>
    );
  }
  return (
    <div className="xl:grid xl:grid-cols-[minmax(0,600px)_minmax(280px,1fr)] xl:items-start xl:gap-8">
      <section className="space-y-4 rounded-[10px] border border-stroke bg-panel p-4 lg:space-y-5 lg:p-6">
        {side && <div className="border-b border-stroke-soft pb-4 xl:hidden">{side}</div>}
        {inReview ? <>{notices}{review}{warnings}</> : children}
        <div className="border-t border-stroke-soft pt-4 lg:flex lg:items-center lg:justify-end lg:gap-4 lg:pt-5">{footer}</div>
        {/* Below xl the desk facts and About come AFTER the action, never between the fields and it (1 Nov 2026 audit). */}
        {!inReview && <div className="xl:hidden">{review}</div>}
        {outcome}
      </section>
      <aside className="hidden min-w-0 space-y-6 xl:block xl:pt-2">{side}{!inReview && review}</aside>
    </div>
  );
}

/** The form's card alone — for a form that has only a line to say (a failed read, nothing to convert). */
export function FormCard({ children }: { children: ReactNode }) {
  return <section className="space-y-4 rounded-[10px] border border-stroke bg-panel p-4 lg:max-w-[600px] lg:space-y-5 lg:p-6">{children}</section>;
}

/** "Before confirming": the conversion read back, then the desk and when the balances were read, then the job's
 *  explanation behind one tap. `children` is the form's own read-back — null before Review: then only the facts and the
 *  fold (no heading, no "nothing to review" line). */
export function Review({ children, desk, readAt, help }: {
  children: ReactNode | null;
  desk: Desk;
  readAt: number;
  /** The job's explanation — behind "About this job" (a help slot: not shown by default). */
  help: ReactNode;
}) {
  return (
    <div className="space-y-3">
      {children != null && <h2 className="text-title font-semibold">Before confirming</h2>}
      {children}
      {/* The desk and the read time live inside About this job: context, not a block of their own (1 Nov 2026 audit). */}
      <Fold label="About this job">
        <div>
          <Fact label="Desk">{DESK_LABEL[desk]}</Fact>
          {readAt > 0 && <Fact label="Read">{fmtTime(readAt)}</Fact>}
        </div>
        {help}
      </Fold>
    </div>
  );
}

/** A quiet fold (Astra's "About this job" / "More"): a chevron and a word; the body under a hairline. */
export function Fold({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-stroke-soft pt-3">
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className="flex min-h-[44px] items-center gap-1 text-body text-tg-hint hover:text-tg-text lg:min-h-[32px]">
        {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
        {label}
      </button>
      {open && <div className="mt-2 space-y-3 text-meta text-tg-hint">{children}</div>}
    </div>
  );
}

/** The footer's primary: "Review conversion" until pressed, then the confirm that posts (it names the figure). The
 *  same disabled-until rule drives both — the review never enables what the form would not post. */
export function ReviewThenConfirm({ reviewing, setReviewing, disabled, pending, confirmLabel, onConfirm, why }: {
  reviewing: boolean;
  setReviewing: (v: boolean) => void;
  disabled: boolean;
  pending: boolean;
  confirmLabel: string;
  onConfirm: () => void;
  /** Why Review is off, in a few words, beside the button (Record's rule; Screens' audit, 27 Oct 2026). */
  why?: string | null;
}) {
  if (!reviewing) {
    const button = (
      <button type="button" className="btn-primary w-full text-body lg:w-auto lg:px-6" disabled={disabled} onClick={() => setReviewing(true)}>
        Review conversion →
      </button>
    );
    if (!(disabled && why && !pending)) return button;
    return (
      <div className="flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-end">
        <p className="text-center text-meta text-tg-hint lg:text-left">{why}</p>
        {button}
      </div>
    );
  }
  return (
    <div className="flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-end">
      {/* A confirm that is off says why beside it — a read that failed after Review, a figure gone stale (Astra, 1 Nov 2026). */}
      {disabled && why && !pending && <p className="text-center text-meta text-tg-hint lg:mr-auto lg:text-left">{why}</p>}
      <button type="button" className="btn-link min-h-[44px] text-body lg:min-h-0" disabled={pending} onClick={() => setReviewing(false)}>Edit</button>
      <button type="button" className="btn-primary w-full text-body lg:w-auto lg:px-6" disabled={disabled} onClick={onConfirm}>
        {pending ? 'Booking…' : confirmLabel}
      </button>
    </div>
  );
}

// ── Recent picks (Astra's "Recent contexts") — this viewer's last three, in this browser only ────────────────────
export type RecentPick = { kind: 'party'; slug: string; name: string } | { kind: 'rail'; slug: string; asset: string; name: string };
const RECENT_KEY = 'otc.convert.recent.v2';

export function readRecent(): RecentPick[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? (v as RecentPick[]).filter((r) => r && typeof r.slug === 'string' && typeof r.name === 'string').slice(0, 3) : [];
  } catch { return []; }
}
export function rememberRecent(p: RecentPick) {
  try {
    const same = (r: RecentPick) => r.kind === p.kind && r.slug === p.slug && (r.kind === 'party' || (p.kind === 'rail' && r.asset === p.asset));   // dialect-ok: the URL's own code, stored as it was picked
    localStorage.setItem(RECENT_KEY, JSON.stringify([p, ...readRecent().filter((r) => !same(r))].slice(0, 3)));
  } catch { /* storage off: nothing remembered, nothing lost */ }
}
